// @services/stubs/StubClientProjectClient.ts
import { Effect } from "effect";
import type { ClientProjectClient } from '@services/clients/ClientProjectClient';
import type {
  Client,
  ClientListResponse,
  CreateClientRequest,
  CreateProjectRequest,
  PresignProjectDocumentsRequest,
  ProjectDocumentListResponse,
  ProjectDocumentPresignResponse,
  Project,
  ProjectListResponse,
  UpdateProjectRequest,
} from "@ps-bridge/models/core/dataContracts";
import type { ClientProjectError } from "@ps-bridge/models/core/dataContracts";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import { makeClientID } from "@ps-bridge/models/brands/clientID";
import { makeProjectID } from "@ps-bridge/models/brands/projectID";
import { makeIsoTimestamp } from "@ps-bridge/models/brands/isoTimestamp";

import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import { effectFromRaw } from '@utils/effects/branded';
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import { effectFail } from "@utils/effects/branded";

const STUB_CLIENT_PROJECT_MODE_KEY = "stub:client-project:mode";
const STUB_CLIENT_PROJECT_ERROR_TYPE_KEY = "stub:client-project:errorType";
const STUB_CLIENT_PROJECT_ERROR_MESSAGE_KEY = "stub:client-project:errorMessage";
const DEFAULT_CLIENT_PROJECT_ERROR_TYPE = "NetworkError";
const DEFAULT_CLIENT_PROJECT_ERROR_MESSAGE = "Unable to load clients.";

const getStubStorageValue = (key: string): OptionTs<string> => {
  if (typeof window === "undefined") {
    return Option.none();
  }
  try {
    const value = window.localStorage.getItem(key);
    return typeof value === "string" ? Option.some(value) : Option.none();
  } catch {
    return Option.none();
  }
};

const getStubClientProjectMode = (): "success" | "error" =>
  Option.fold(
    getStubStorageValue(STUB_CLIENT_PROJECT_MODE_KEY),
    () => "success",
    (value) => (value === "error" ? "error" : "success"),
  );

const buildClientProjectError = (): ClientProjectError => ({
  type: Option.fold(
    getStubStorageValue(STUB_CLIENT_PROJECT_ERROR_TYPE_KEY),
    () => DEFAULT_CLIENT_PROJECT_ERROR_TYPE,
    (value) => value,
  ),
  message: makeErrorMessageText(
    Option.fold(
      getStubStorageValue(STUB_CLIENT_PROJECT_ERROR_MESSAGE_KEY),
      () => DEFAULT_CLIENT_PROJECT_ERROR_MESSAGE,
      (value) => value,
    ),
  ),
  statusCode: Option.none(),
});

export class StubClientProjectClient implements ClientProjectClient {
  private clients: Client[] = [];
  private projects: Project[] = [];
  private clientSequence = 0;
  private projectSequence = 0;

  listClients = effectFromRaw<ClientListResponse, ClientProjectError>(
    Effect.suspend(() =>
      getStubClientProjectMode() === "error"
        ? Effect.fail(buildClientProjectError())
        : Effect.succeed({ items: this.clients }),
    ),
  );

  createClient(request: CreateClientRequest) {
    if (getStubClientProjectMode() === "error") {
      return effectFail<Ok, ClientProjectError>(buildClientProjectError());
    }
    this.clientSequence += 1;
    this.clients = [
      ...this.clients,
      {
        id: makeClientID(`stub-client-${this.clientSequence}`),
        organization_id: "stub-organization-id" as never,
        name: request.name,
        created_at: makeIsoTimestamp(new Date().toISOString()),
      },
    ];
    return effectFromRaw<Ok, ClientProjectError>(Effect.succeed(ok));
  }

  listProjects(clientId: string) {
    return effectFromRaw<ProjectListResponse, ClientProjectError>(
      Effect.succeed({
        items: this.projects.filter((project) =>
          Option.fold(project.client_id, () => false, (projectClientId) => String(projectClientId) === clientId),
        ),
      }),
    );
  }

  createProject(clientId: string, request: CreateProjectRequest) {
    if (getStubClientProjectMode() === "error") {
      return effectFail<Ok, ClientProjectError>(buildClientProjectError());
    }
    this.projectSequence += 1;
    const timestamp = makeIsoTimestamp(new Date().toISOString());
    this.projects = [
      ...this.projects,
      {
        id: makeProjectID(`stub-project-${this.projectSequence}`),
        client_id: Option.some(makeClientID(clientId)),
        organization_id: "stub-organization-id" as never,
        name: request.name,
        created_at: timestamp,
        updated_at: timestamp,
        last_updated_at: timestamp,
      },
    ];
    return effectFromRaw<Ok, ClientProjectError>(Effect.succeed(ok));
  }

  updateProject(_projectId: string, _request: UpdateProjectRequest) {
    return effectFromRaw<Ok, ClientProjectError>(Effect.succeed(ok));
  }

  deleteProject(_projectId: string) {
    return effectFromRaw<Ok, ClientProjectError>(Effect.succeed(ok));
  }

  listProjectDocuments(_projectId: string) {
    return effectFromRaw<ProjectDocumentListResponse, ClientProjectError>(Effect.succeed({ items: [] }));
  }

  presignProjectDocuments(
    _clientId: string,
    _projectId: string,
    _request: PresignProjectDocumentsRequest
  ) {
    return effectFromRaw<ProjectDocumentPresignResponse, ClientProjectError>(Effect.succeed({ items: [] }));
  }

  deleteProjectDocument(_projectId: string, _documentId: string) {
    return effectFromRaw<Ok, ClientProjectError>(Effect.succeed(ok));
  }
}
